import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import { TAXI_AGREMENT_BADGE } from '../constants/branding';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [settings, setSettings] = useState({
    // Tarifs
    baseFare: 7.00,
    dayRate: 1.20,
    nightRate: 1.50,
    waitingRate: 35.00,
    airportSurcharge: 5.00,
    
    // Apparence
    primaryColor: '#014421',
    primaryLight: '#026b2f',
    companyName: 'TAXI ABEL',
    companySlogan: 'Taxi professionnel à La Grand-Croix',
    
    // Informations de contact
    phone: '+33477123456',
    email: 'contact@taxiabel.fr',
    address: 'La Grand-Croix (42320)',
    
    // Lieux
    locations: [
      'La Grand-Croix',
      'Saint-Étienne',
      'Rive-de-Gier',
      'Lorette',
      'Lyon-Saint-Exupéry',
      'Grenoble',
      'CHU Saint-Étienne',
      'Gare Saint-Étienne',
      'Aéroport Saint-Étienne'
    ],
    
    // Distances (en km)
    distances: {
      'La Grand-Croix': {
        'Saint-Étienne': 12,
        'Rive-de-Gier': 8,
        'Lorette': 15,
        'Lyon-Saint-Exupéry': 65,
        'Grenoble': 85,
        'CHU Saint-Étienne': 10,
        'Gare Saint-Étienne': 12,
        'Aéroport Saint-Étienne': 25
      },
      'Saint-Étienne': {
        'La Grand-Croix': 12,
        'Rive-de-Gier': 20,
        'Lorette': 25,
        'Lyon-Saint-Exupéry': 75,
        'Grenoble': 95,
        'CHU Saint-Étienne': 5,
        'Gare Saint-Étienne': 2,
        'Aéroport Saint-Étienne': 15
      },
      'Rive-de-Gier': {
        'La Grand-Croix': 8,
        'Saint-Étienne': 20,
        'Lorette': 12,
        'Lyon-Saint-Exupéry': 70,
        'Grenoble': 90,
        'CHU Saint-Étienne': 18,
        'Gare Saint-Étienne': 20,
        'Aéroport Saint-Étienne': 30
      }
    }
  });

  // Charger les paramètres après le first paint (évite de bloquer le thread principal)
  useEffect(() => {
    apiService.clearLegacyTokens();

    const schedulePublicSettingsLoad = () => {
      loadPublicSettings();
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      window.requestIdleCallback(schedulePublicSettingsLoad, { timeout: 2500 });
    } else {
      window.setTimeout(schedulePublicSettingsLoad, 800);
    }

    apiService.verifyToken()
      .then((response) => {
        setIsAuthenticated(true);
        setAdminData(response.user);
        loadSettingsFromAPI();
      })
      .catch(() => {
        setIsAuthenticated(false);
        setAdminData(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Charger les paramètres publics depuis l'API (sans authentification)
  const loadPublicSettings = async () => {
    try {
      const [companyInfo, appearance, pricing, locations] = await Promise.all([
        apiService.getCompanyInfo(),
        apiService.getSetting('appearance'),
        apiService.getPricing(),
        apiService.getLocations()
      ]);

      // Mettre à jour les paramètres
      setSettings(prev => ({
        ...prev,
        companyName: companyInfo?.name || 'TAXI ABEL',
        companySlogan: companyInfo?.slogan || 'Taxi professionnel à La Grand-Croix',
        phone: companyInfo?.phone || '+33477123456',
        email: companyInfo?.email || 'contact@taxiabel.fr',
        address: companyInfo?.address || 'La Grand-Croix (42320)',
        siren: companyInfo?.siren || '919 966 374',
        siret: companyInfo?.siret || '919 966 374 00010',
        rcs: companyInfo?.rcs || 'RCS Saint-Étienne 919 966 374',
        tva: companyInfo?.tva || 'FR38 919966374',
        capital: companyInfo?.capital || '3 000 €',
        primaryColor: appearance?.primary_color || '#014421',
        primaryLight: appearance?.primary_light || '#026b2f',
        footerColor: appearance?.footer_color || '#1a1a1a',
        buttonColor: appearance?.button_color || '#014421',
        buttonHoverColor: appearance?.button_hover_color || '#026b2f',
        // Paramètres hero
        heroTitle: appearance?.hero_title || 'TAXI ABEL',
        heroSubtitle: appearance?.hero_subtitle || 'Taxi professionnel à La Grand-Croix',
        heroEyebrow: appearance?.hero_eyebrow || TAXI_AGREMENT_BADGE,
        heroDescription: appearance?.hero_description || 'Service de transport professionnel basé à La Grand-Croix, desservant Saint-Étienne, Rive-de-Gier, Lorette et toutes les communes alentours. Disponible 24h/7j.',
        heroBackgroundImage: appearance?.hero_background_image
          ? String(appearance.hero_background_image).replace(/&#x2F;/g, '/')
          : '',
        button1Text: appearance?.button1_text || 'Calculer un tarif',
        button1Color: appearance?.button1_color || '#dc3545',
        button1TextColor: appearance?.button1_text_color || '#ffffff',
        button2Text: appearance?.button2_text || 'Appeler maintenant',
        button2Color: appearance?.button2_color || '#ffffff',
        button2TextColor: appearance?.button2_text_color || '#dc3545',
        baseFare: pricing?.base_fare || 7.00,
        dayRate: pricing?.day_rate || 1.20,
        nightRate: pricing?.night_rate || 1.50,
        waitingRate: pricing?.waiting_rate || 35.00,
        airportSurcharge: pricing?.airport_surcharge || 5.00,
        locations: locations || []
      }));

      // Appliquer les couleurs CSS
      if (appearance?.primary_color) {
        document.documentElement.style.setProperty('--primary-color', appearance.primary_color);
      }
      if (appearance?.primary_light) {
        document.documentElement.style.setProperty('--primary-light', appearance.primary_light);
      }
      if (appearance?.text_color) {
        document.documentElement.style.setProperty('--text-color', appearance.text_color);
      }
      if (appearance?.footer_color) {
        document.documentElement.style.setProperty('--footer-bg', appearance.footer_color);
      }
      if (appearance?.button_color) {
        document.documentElement.style.setProperty('--button-bg', appearance.button_color);
      }
      if (appearance?.button_hover_color) {
        document.documentElement.style.setProperty('--button-hover', appearance.button_hover_color);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des paramètres publics:', error);
    }
  };

  // Charger les paramètres depuis l'API (avec authentification)
  const loadSettingsFromAPI = async () => {
    try {
      // Charger les informations de la société
      const companyInfo = await apiService.getCompanyInfo();
      
      // Charger les couleurs
      const appearance = await apiService.getSetting('appearance');
      
      // Charger les tarifs
      const pricing = await apiService.getPricing();
      
      // Charger les lieux
      const locations = await apiService.getLocations();

      // Mettre à jour les paramètres
      setSettings(prev => ({
        ...prev,
        companyName: companyInfo?.name || 'TAXI ABEL',
        companySlogan: companyInfo?.slogan || 'Taxi professionnel à La Grand-Croix',
        phone: companyInfo?.phone || '+33477123456',
        email: companyInfo?.email || 'contact@taxiabel.fr',
        address: companyInfo?.address || 'La Grand-Croix (42320)',
        siren: companyInfo?.siren || '919 966 374',
        siret: companyInfo?.siret || '919 966 374 00010',
        rcs: companyInfo?.rcs || 'RCS Saint-Étienne 919 966 374',
        tva: companyInfo?.tva || 'FR38 919966374',
        capital: companyInfo?.capital || '3 000 €',
        primaryColor: appearance?.primary_color || '#014421',
        primaryLight: appearance?.primary_light || '#026b2f',
        footerColor: appearance?.footer_color || '#1a1a1a',
        buttonColor: appearance?.button_color || '#014421',
        buttonHoverColor: appearance?.button_hover_color || '#026b2f',
        // Paramètres hero
        heroTitle: appearance?.hero_title || 'TAXI ABEL',
        heroSubtitle: appearance?.hero_subtitle || 'Taxi professionnel à La Grand-Croix',
        heroEyebrow: appearance?.hero_eyebrow || TAXI_AGREMENT_BADGE,
        heroDescription: appearance?.hero_description || 'Service de transport professionnel basé à La Grand-Croix, desservant Saint-Étienne, Rive-de-Gier, Lorette et toutes les communes alentours. Disponible 24h/7j.',
        heroBackgroundImage: appearance?.hero_background_image
          ? String(appearance.hero_background_image).replace(/&#x2F;/g, '/')
          : '',
        button1Text: appearance?.button1_text || 'Calculer un tarif',
        button1Color: appearance?.button1_color || '#dc3545',
        button1TextColor: appearance?.button1_text_color || '#ffffff',
        button2Text: appearance?.button2_text || 'Appeler maintenant',
        button2Color: appearance?.button2_color || '#ffffff',
        button2TextColor: appearance?.button2_text_color || '#dc3545',
        baseFare: pricing?.base_fare || 7.00,
        dayRate: pricing?.day_rate || 1.20,
        nightRate: pricing?.night_rate || 1.50,
        waitingRate: pricing?.waiting_rate || 35.00,
        airportSurcharge: pricing?.airport_surcharge || 5.00,
        locations: locations || []
      }));

      // Appliquer les couleurs CSS
      if (appearance?.primary_color) {
        document.documentElement.style.setProperty('--primary-color', appearance.primary_color);
      }
      if (appearance?.primary_light) {
        document.documentElement.style.setProperty('--primary-light', appearance.primary_light);
      }
      if (appearance?.text_color) {
        document.documentElement.style.setProperty('--text-color', appearance.text_color);
      }
      if (appearance?.footer_color) {
        document.documentElement.style.setProperty('--footer-bg', appearance.footer_color);
      }
      if (appearance?.button_color) {
        document.documentElement.style.setProperty('--button-bg', appearance.button_color);
      }
      if (appearance?.button_hover_color) {
        document.documentElement.style.setProperty('--button-hover', appearance.button_hover_color);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paramètres:', error);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await apiService.login(username, password);
      setIsAuthenticated(true);
      setAdminData(response.user);
      return true;
    } catch (error) {
      console.error('Erreur de connexion:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      setIsAuthenticated(false);
      setAdminData(null);
    }
  };

  const updateSettings = (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));
    
    // Appliquer les nouvelles couleurs CSS
    if (newSettings.footerColor) {
      document.documentElement.style.setProperty('--footer-bg', newSettings.footerColor);
    }
    if (newSettings.buttonColor) {
      document.documentElement.style.setProperty('--button-bg', newSettings.buttonColor);
    }
    if (newSettings.buttonHoverColor) {
      document.documentElement.style.setProperty('--button-hover', newSettings.buttonHoverColor);
    }
    if (newSettings.primaryColor) {
      document.documentElement.style.setProperty('--primary-color', newSettings.primaryColor);
    }
    if (newSettings.primaryLight) {
      document.documentElement.style.setProperty('--primary-light', newSettings.primaryLight);
    }
    if (newSettings.textColor) {
      document.documentElement.style.setProperty('--text-color', newSettings.textColor);
    }
    
    // En production, sauvegarder en base de données
    localStorage.setItem('taxiSettings', JSON.stringify({ ...settings, ...newSettings }));
  };

  // Fonction pour recharger les paramètres depuis l'API
  const reloadSettings = async () => {
    if (isAuthenticated) {
      await loadSettingsFromAPI();
      // Les couleurs sont déjà appliquées dans loadSettingsFromAPI
    }
  };

  const addLocation = (location) => {
    if (!settings.locations.includes(location)) {
      const newLocations = [...settings.locations, location];
      updateSettings({ locations: newLocations });
    }
  };

  const removeLocation = (location) => {
    const newLocations = settings.locations.filter(loc => loc !== location);
    updateSettings({ locations: newLocations });
  };

  const updateDistance = (from, to, distance) => {
    const newDistances = { ...settings.distances };
    if (!newDistances[from]) {
      newDistances[from] = {};
    }
    newDistances[from][to] = distance;
    updateSettings({ distances: newDistances });
  };

  const value = {
    isAuthenticated,
    adminData,
    settings,
    login,
    logout,
    updateSettings,
    reloadSettings,
    addLocation,
    removeLocation,
    updateDistance
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
